import React, { useEffect, useState } from 'react';
import { Button, Col, OverlayTrigger, Popover, Row } from 'react-bootstrap';
import i18n from '../../../../i18n';
import PopoverContent from '../../../overlays/Popover/PopoverContent';
import clsx from 'clsx';
import styles from '../../../ui/servicePicker/ServicePicker.module.css';
import BlazeInputDropdown, { BlazeDropdownItem } from '../../../ui/dropdown/BlazeDropdown/BlazeInputDropdown';
import Form from 'react-bootstrap/Form';
import CheckBox from '../../../ui/checkBox/CheckBox';
import _ from 'lodash';
import { useIsMount } from '../../../../hooks/useIsMountHook';

export type AttributeMatch = { type: string; value: string };

interface AttributeMatcherPopoverProps {
  nodeTypes: BlazeDropdownItem[];
  onChangeHandler: (attribute: AttributeMatch | null) => void;
  selectedMatchValue: string;
  selectedMatchType: BlazeDropdownItem;
  isSelected: boolean;
  attrName: string;
  isInline: boolean;
  /** Helper function which helps to propagate inner state of the child popover overlay. */
  onDropdownOpen: (isOpen: boolean) => void;
}

//No Matching default item
const noMatchingItem: BlazeDropdownItem = { name: '- No Matching - ', id: -1 };

const AttributeNode = (props: AttributeMatcherPopoverProps) => {
  const [matchingValue, setMatchingValue] = useState<string>(props.selectedMatchValue);
  const [matchingType, setMatchingType] = useState<BlazeDropdownItem>(
    props.isSelected ? props.selectedMatchType : noMatchingItem,
  );
  const [dropdownItems, setDropdownItems] = useState([]);
  const isMount = useIsMount();

  const buildDropdownItems = () => {
    //Add option for "No Matching"
    const menuItems = _.cloneDeep(props.nodeTypes);
    menuItems.unshift(noMatchingItem);
    setDropdownItems(menuItems);
  };

  useEffect(() => {
    if (isMount) {
      buildDropdownItems();
    }
  }, []);

  useEffect(() => {
    if (!isMount) {
      setMatchingValue(props.selectedMatchValue);
    }
  }, [props.selectedMatchValue]);

  useEffect(() => {
    if (!isMount) {
      setMatchingType(props.isSelected ? props.selectedMatchType : noMatchingItem);
    }
  }, [props.isSelected]);

  const getPopover = () => {
    return (
      <Popover style={{ minWidth: 320 }} id={'popoverId'} title={i18n.t('servicePicker.serviceId')}>
        <PopoverContent>
          <PopoverContent.Header>
            <label className={'font-size-lg font-weight-semibold mb-0'}>
              {i18n.t('transactions:editor.attribute.title')}
            </label>
            <button
              className={clsx('close', styles.closeButton)}
              onClick={() => document.body.click()}
              type="button"
              data-dismiss="modal"
              aria-label="Close"
              style={{ marginTop: '-2px' }}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </PopoverContent.Header>
          <PopoverContent.Body>
            <Row>
              <Col>
                <span>{i18n.t('transactions:editor.matchType')}</span>
                <BlazeInputDropdown
                  id={'attribute-match-type-picker'}
                  showGroups={false}
                  isSearchable={false}
                  minDropdownMenuWidth={150}
                  maxDropdownHeight={200}
                  items={dropdownItems}
                  selected={matchingType}
                  onClick={(item: BlazeDropdownItem) => setMatchingType(item)}
                  isDefaultOptionSelectable={true}
                />
              </Col>
              <Col>
                <span>{i18n.t('transactions:editor.matchValue')}</span>
                <Form.Control
                  value={matchingValue}
                  title={matchingValue}
                  // Disabled if Anything or - No Matching - is selected
                  disabled={matchingType.name === 'Anything' || matchingType.id == -1}
                  onChange={(event: any) => {
                    setMatchingValue(event.target.value);
                  }}
                />
              </Col>
            </Row>
          </PopoverContent.Body>
          <PopoverContent.Footer>
            <div className={'text-right'}>
              <Button
                id={'cancel-txn-save-btn'}
                variant="link"
                onClick={() => {
                  document.body.click();
                }}
              >
                {i18n.t('common:actions.cancel')}
              </Button>
              <Button
                id={'approve-txn-save-btn'}
                variant="primary"
                onClick={() => {
                  //No matching is selected
                  if (matchingType.id === -1) {
                    props.onChangeHandler(null);
                  } else {
                    props.onChangeHandler({ value: matchingValue, type: matchingType.id as string });
                  }
                  document.body.click();
                }}
              >
                Save
              </Button>
            </div>
          </PopoverContent.Footer>
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <OverlayTrigger
      trigger={'click'}
      placement={props.isInline ? 'bottom' : 'left'}
      rootClose={true}
      overlay={getPopover()}
      onToggle={isVisible => {
        props.onDropdownOpen(isVisible);
      }}
    >
      <div className={props.isInline ? 'd-inline-block' : 'p-2'}>
        <CheckBox
          labelClassName={'text-primary font-weight-semi-bold'}
          checked={props.isSelected}
          label={props.attrName}
          onCheck={_.noop}
        />
      </div>
    </OverlayTrigger>
  );
};

export default AttributeNode;
